import React, { useEffect, useCallback, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { FormInput, Margin, Text } from 'shared/components'
import { EditableFormPanel } from 'routes/shared/components'
import { dateWithTime } from 'constants/dateFormats'
import AsyncSelect from 'react-select/async'
import { components } from 'react-select'

import * as styles from './styles'
import {
  getTournamentLabel,
  getTeamNameLabel,
} from './helpers'

const getOptionBackgroundColor = ({ isFocused, isSelected }) => {
  if (isSelected) {
    return '#3e3e3e'
  }

  if (isFocused) {
    return '#383838'
  }

  return '#2a2a2a'
}

const EditEventFormComponent = ({
  form,
  header,
  loadJurisdictions,
  loadTournaments,
  loadTeamsSearch,
  loadCategories,
  onSubmitRequest,
  onChangeTournament,
  teams,
}) => {
  // State to track input values for dropdowns
  const [inputValue, setInputValue] = useState('');
  const [homeTeamInput, setHomeTeamInput] = useState('');
  const [awayTeamInput, setAwayTeamInput] = useState('');

  // Local state to track the tournament selection for UI rendering
  const [tournamentSelection, setTournamentSelection] = useState(form.fields.tournamentId.value);
  const [homeTeamSelection, setHomeTeamSelection] = useState(form.fields.homeTeamId.value);
  const [awayTeamSelection, setAwayTeamSelection] = useState(form.fields.awayTeamId.value);

  // Render key to force re-render when needed
  const [renderKey, setRenderKey] = useState(0);
  let sportTypeName = ''

  if (form.fields.sportType.value?.name) {
    sportTypeName = form.fields.sportType.value.name
  } else if (form.fields.tournamentId.value?.sportType) {
    sportTypeName = form.fields.tournamentId.value.sportType.charAt(0).toUpperCase() +
      form.fields.tournamentId.value.sportType.slice(1).toLowerCase()
  }

  // Update local state when form value changes
  useEffect(() => {
    setTournamentSelection(form.fields.tournamentId.value);
    setHomeTeamSelection(form.fields.homeTeamId.value);
    setAwayTeamSelection(form.fields.awayTeamId.value);
  }, [form.fields.tournamentId.value, form.fields.homeTeamId.value, form.fields.awayTeamId.value]);

  // Track if component is mounted
  const isMountedRef = useRef(true);

  // Cleanup function to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, [form]);

  // Function to load tournaments with debounced search
  const debouncedLoadTournaments = useCallback((inputText) => {
    // Don't proceed if component is unmounted
    if (!isMountedRef.current) {
      return Promise.resolve([]);
    }

    if (!inputText || inputText.length < 2) {
      return Promise.resolve([]);
    }

    return loadTournaments(inputText)
      .then(results => {
        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) {
          return [];
        }

        // Return the results directly, no client-side filtering needed
        return results || [];
      })
      .catch(err => {
        console.error('Search error:', err);
        return [];
      });
  }, [loadTournaments, isMountedRef]);

  // Function to load teams with debounced search
  const debouncedLoadTeams = useCallback((inputText) => {
    // Don't proceed if component is unmounted
    if (!isMountedRef.current) {
      return Promise.resolve([]);
    }

    if (!inputText || inputText.length < 2) {
      return Promise.resolve([]);
    }

    // Get current sportType from the selected tournament
    const tournamentValue = form.fields.tournamentId.value;

    if (!tournamentValue) {
      return Promise.resolve([]);
    }

    // Extract sportType - check both direct property and potentially nested sportType
    let sportType = null;

    // Try multiple potential locations for the sport type
    if (tournamentValue.sportType) {
      sportType = tournamentValue.sportType;
    } else if (tournamentValue.sport) {
      sportType = tournamentValue.sport;
    } else if (form.fields.sportType.value) {
      sportType = form.fields.sportType.value.id;
    }

    if (!sportType) {
      return Promise.resolve([]);
    }

    return loadTeamsSearch(inputText, sportType)
      .then(results => {
        // Check if component is still mounted before proceeding
        if (!isMountedRef.current) {
          return [];
        }

        return results || [];
      })
      .catch(err => {
        console.error('Team search error:', err);
        return [];
      });
  }, [loadTeamsSearch, form.fields.tournamentId.value, form.fields.sportType.value, isMountedRef]);

  // Create a stable reference for an option label cache to prevent excessive re-renders
  const optionLabelCache = useRef(new Map());
  const teamLabelCache = useRef(new Map());

  // Memoize option rendering functions to reduce re-renders
  const getOptionLabel = useCallback((option) => {
    // Use cached value if available to prevent re-renders
    if (option.id && optionLabelCache.current.has(option.id)) {
      return optionLabelCache.current.get(option.id);
    }

    // Calculate the label
    const label = option.name || option.tournamentName || 'Unnamed tournament';

    // Store in cache
    if (option.id) {
      optionLabelCache.current.set(option.id, label);
    }

    return label;
  }, []);

  // Memoize team option rendering functions
  const getTeamOptionLabel = useCallback((option) => {
    // Use cached value if available to prevent re-renders
    if (option.id && teamLabelCache.current.has(option.id)) {
      return teamLabelCache.current.get(option.id);
    }

    // Calculate the label - if using placeholder, wrap in span with placeholder color
    if (!option.teamName) {
      // Return object with formatted text for custom rendering
      return <span style={{ color: '#757575' }}>Search teams</span>;
    }

    const label = option.teamName;

    // Store in cache
    if (option.id) {
      teamLabelCache.current.set(option.id, label);
    }

    return label;
  }, []);

  const getOptionValue = useCallback((option) => {
    return option.id?.toString();
  }, []);

  // Handle tournament selection changes
  const handleTournamentChange = useCallback((selectedOption) => {
    // Update form value - explicitly handle null for clearable

    // Update local state first for immediate UI feedback
    setTournamentSelection(selectedOption);

    // Set the form value 
    form.fields.tournamentId.onChange({ target: { value: selectedOption || null } });

    // Then notify parent - parent will handle clearing related fields
    onChangeTournament(selectedOption);

    // Don't clear team selections here - parent onChangeTournament will handle that
    // to avoid duplicate clearing operations
  }, [form, onChangeTournament]);

  // Force clear tournament - use a more direct approach
  const handleClearTournament = useCallback(() => {

    // Directly clear local selection state
    setTournamentSelection(null);

    // Directly clear form state
    form.fields.tournamentId.onChange({ target: { value: null } });
    form.fields.homeTeamId.onChange({ target: { value: null } });
    form.fields.awayTeamId.onChange({ target: { value: null } });
    form.fields.sportType.onChange({ target: { value: null } });
    form.fields.jurisdiction.onChange({ target: { value: '' } });

    // Reset input value
    setInputValue('');

    // Force re-render by incrementing the key
    setRenderKey(prev => prev + 1);

    // Force update any select refs to ensure visual clearing
    if (window.document.getElementById('direct-tournament-select')) {
      const selectElement = window.document.getElementById('direct-tournament-select');
      if (selectElement) {
        // Attempt to trigger clear programmatically
        const inputElement = selectElement.querySelector('input');
        if (inputElement) {
          inputElement.value = '';
        }
      }
    }
  }, [form.fields]);

  // Handle home team selection changes
  const handleHomeTeamChange = useCallback((selectedOption) => {
    setHomeTeamSelection(selectedOption);
    form.fields.homeTeamId.onChange({ target: { value: selectedOption || null } });
  }, [form]);

  // Handle away team selection changes
  const handleAwayTeamChange = useCallback((selectedOption) => {
    setAwayTeamSelection(selectedOption);
    form.fields.awayTeamId.onChange({ target: { value: selectedOption || null } });
  }, [form]);

  // Prepare tournament options to be more React-friendly
  const prepareOptions = useCallback((options) => {
    return options.map(option => ({
      ...option,
      // Adding a unique key helps React-Select with memoization
      key: `tournament-${option.id}`
    }));
  }, []);

  // Handle input changes in the tournament search
  const handleTournamentInput = useCallback((text, action) => {
    setInputValue(text);
    return text;
  }, [setInputValue]);

  // Handle input changes in the home team search
  const handleHomeTeamInput = useCallback((text, action) => {
    setHomeTeamInput(text);
    return text;
  }, [setHomeTeamInput]);

  // Handle input changes in the away team search
  const handleAwayTeamInput = useCallback((text, action) => {
    setAwayTeamInput(text);
    return text;
  }, [setAwayTeamInput]);

  // Memoize the no options message function
  const noOptionsMessage = useCallback(({ inputValue: text }) => {
    if (!text || text.length < 2) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Type at least 2 characters to search</span>;
    }
    return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Loading...</span>;
  }, []);

  // No teams message for when tournament is not selected
  const noTeamsMessage = useCallback(({ inputValue: text }) => {
    if (!form.fields.tournamentId.value) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Select a tournament first</span>;
    }
    if (!text || text.length < 2) {
      return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Type at least 2 characters to search</span>;
    }
    return <span style={{ fontFamily: 'Montserrat, Arial, Helvetica, sans-serif', fontSize: '10px' }}>Loading...</span>;
  }, [form.fields.tournamentId.value]);

  // Team options loader with better sportType handling
  const loadTeamOptions = useCallback((inputText, { fieldId } = {}) => {
    // Get tournament data first to check its structure
    const tournament = form.fields.tournamentId.value;

    if (!tournament?.id) {
      return Promise.resolve([]);
    }

    return debouncedLoadTeams(inputText)
      .then(results => {
        // For away team selection, filter out the home team if it's already selected
        if (fieldId === 'awayTeamId' && form.fields.homeTeamId.value?.id) {
          return results.filter(team =>
            team.id !== form.fields.homeTeamId.value.id
          );
        }
        return results;
      });
  }, [debouncedLoadTeams, form.fields.tournamentId.value, form.fields.homeTeamId.value]);

  // Modified loadOptions function to prepare the options
  const loadTournamentOptions = useCallback((inputText) => {
    return debouncedLoadTournaments(inputText)
      .then(options => {
        return prepareOptions(options);
      });
  }, [debouncedLoadTournaments, prepareOptions]);

  return (
    <EditableFormPanel header={header} form={form} onSubmit={onSubmitRequest}>
      {(editable) => (
        <>
          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Tournament
            </Text>
            <div style={{ width: '180px' }}>
              <AsyncSelect
                key={`tournament-select-${renderKey}`}
                id="direct-tournament-select"
                cacheOptions={false}
                defaultOptions={false}
                isSearchable={true}
                loadOptions={loadTournamentOptions}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                onChange={handleTournamentChange}
                onInputChange={handleTournamentInput}
                isClearable={true}
                placeholder="Search tournaments..."
                noOptionsMessage={noOptionsMessage}
                value={tournamentSelection}
                menuIsOpen={inputValue && inputValue.length >= 2 ? true : undefined}
                isDisabled={!editable}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '100%',
                  }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    borderColor: '#3e3e3e',
                    minHeight: '40px',
                    height: '40px',
                    boxShadow: 'none',
                    borderRadius: '4px',
                    '&:hover': {
                      borderColor: '#4a4a4a'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    zIndex: 9999,
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '150px',
                    padding: '4px 0'
                  }),
                  option: (base, { isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                    color: '#ffffff',
                    '&:active': {
                      backgroundColor: '#3e3e3e'
                    },
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  input: (base) => ({
                    ...base,
                    color: '#ffffff',
                    margin: '0',
                    padding: '0',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#757575',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#ffffff',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    backgroundColor: '#3e3e3e'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#757575',
                    padding: '8px',
                    '&:hover': {
                      color: '#ffffff'
                    },
                    fontSize: '10px'
                  }),
                  clearIndicator: (base) => ({
                    ...base,
                    color: '#757575',
                    padding: '8px',
                    '&:hover': {
                      color: '#ffffff'
                    },
                    fontSize: '10px'
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                    fontSize: '10px'
                  })
                }}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Event Name
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                name="eventName"
                type="text"
                placeholder="Enter event name"
                css={styles.dropdownControl}
                disabled={!editable}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Home team
            </Text>
            <div style={{ width: '180px' }}>
              <AsyncSelect
                key={`home-team-select-${renderKey}`}
                id="home-team-select"
                cacheOptions={false}
                defaultOptions={false}
                isSearchable={true}
                loadOptions={(inputText) => loadTeamOptions(inputText, { fieldId: 'homeTeamId' })}
                getOptionLabel={getTeamOptionLabel}
                getOptionValue={getOptionValue}
                onChange={handleHomeTeamChange}
                onInputChange={handleHomeTeamInput}
                isClearable={true}
                placeholder="Search home team..."
                noOptionsMessage={noTeamsMessage}
                value={homeTeamSelection}
                menuIsOpen={homeTeamInput && homeTeamInput.length >= 2 && form.fields.tournamentId.value ? true : undefined}
                isDisabled={!editable || !form.fields.tournamentId.value}
                filterOption={() => true}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '180px',
                  }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    borderColor: '#3e3e3e',
                    minHeight: '40px',
                    height: '40px',
                    boxShadow: 'none',
                    borderRadius: '4px',
                    width: '180px',
                    '&:hover': {
                      borderColor: '#4a4a4a'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    zIndex: 9999,
                    width: '180px'
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '150px',
                    padding: '4px 0'
                  }),
                  option: (base, { isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                    color: '#ffffff',
                    '&:active': {
                      backgroundColor: '#3e3e3e'
                    },
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  input: (base) => ({
                    ...base,
                    color: '#ffffff',
                    margin: '0',
                    padding: '0',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#757575',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#ffffff',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    backgroundColor: '#3e3e3e'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#757575',
                    padding: '8px',
                    '&:hover': {
                      color: '#ffffff'
                    },
                    fontSize: '10px'
                  }),
                  clearIndicator: (base) => ({
                    ...base,
                    color: '#757575',
                    padding: '8px',
                    '&:hover': {
                      color: '#ffffff'
                    },
                    fontSize: '10px'
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                    fontSize: '10px'
                  })
                }}
              />
            </div>
          </Margin>
          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Away team
            </Text>
            <div style={{ width: '180px' }}>
              <AsyncSelect
                key={`away-team-select-${renderKey}`}
                id="away-team-select"
                cacheOptions={false}
                defaultOptions={false}
                isSearchable={true}
                loadOptions={(inputText) => loadTeamOptions(inputText, { fieldId: 'awayTeamId' })}
                getOptionLabel={getTeamOptionLabel}
                getOptionValue={getOptionValue}
                onChange={handleAwayTeamChange}
                onInputChange={handleAwayTeamInput}
                isClearable={true}
                placeholder="Search away team..."
                noOptionsMessage={noTeamsMessage}
                value={awayTeamSelection}
                menuIsOpen={awayTeamInput && awayTeamInput.length >= 2 && form.fields.tournamentId.value ? true : undefined}
                isDisabled={!editable || !form.fields.tournamentId.value}
                filterOption={() => true}
                styles={{
                  container: (base) => ({
                    ...base,
                    width: '180px',
                  }),
                  control: (base, state) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    borderColor: '#3e3e3e',
                    minHeight: '40px',
                    height: '40px',
                    boxShadow: 'none',
                    borderRadius: '4px',
                    width: '180px',
                    '&:hover': {
                      borderColor: '#4a4a4a'
                    }
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: '#2a2a2a',
                    zIndex: 9999,
                    width: '180px'
                  }),
                  menuList: (base) => ({
                    ...base,
                    maxHeight: '150px',
                    padding: '4px 0'
                  }),
                  option: (base, { isFocused, isSelected }) => ({
                    ...base,
                    backgroundColor: getOptionBackgroundColor({ isFocused, isSelected }),
                    color: '#ffffff',
                    '&:active': {
                      backgroundColor: '#3e3e3e'
                    },
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  input: (base) => ({
                    ...base,
                    color: '#ffffff',
                    margin: '0',
                    padding: '0',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: '#757575',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: '#ffffff',
                    fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
                    fontSize: '10px'
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    backgroundColor: '#3e3e3e'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: '#757575',
                    padding: '8px',
                    '&:hover': {
                      color: '#ffffff'
                    },
                    fontSize: '10px'
                  }),
                  clearIndicator: (base) => ({
                    ...base,
                    color: '#757575',
                    padding: '8px',
                    '&:hover': {
                      color: '#ffffff'
                    },
                    fontSize: '10px'
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 8px',
                    fontSize: '10px'
                  })
                }}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Sport
            </Text>
            <div style={{
              backgroundColor: '#2a2a2a',
              borderColor: '#3e3e3e',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderRadius: '4px',
              padding: '0 8px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              width: '162px',
              color: '#ffffff',
              fontFamily: 'Montserrat, Arial, Helvetica, sans-serif',
              fontSize: '10px'
            }}>
              {sportTypeName}
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Jurisdiction
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                oldInput
                strictLong
                cacheOptions
                defaultOptions
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                type="async-select"
                name="jurisdiction"
                placeholder="Jurisdiction"
                loadOptions={loadJurisdictions}
                css={styles.dropdownControl}
                disabled={!editable}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Start date
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                name="startTime"
                type="date"
                placeholder="MM/dd/yyyy"
                css={styles.dropdownControl}
                disabled={!editable}
                options={{
                  showTimeInput: false,
                  showYearDropdown: true,
                  dateFormat: "MM/dd/yyyy",
                  placeholderText: "MM/dd/yyyy"
                }}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Start time
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                name="startTimeHour"
                type="time"
                placeholder="HH:mm"
                css={styles.dropdownControl}
                disabled={!editable}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              End date
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                name="endTime"
                type="date"
                placeholder="MM/dd/yyyy"
                css={styles.dropdownControl}
                disabled={!editable}
                options={{
                  showTimeInput: false,
                  showYearDropdown: true,
                  dateFormat: "MM/dd/yyyy",
                  placeholderText: "MM/dd/yyyy"
                }}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              End time
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                name="endTimeHour"
                type="time"
                placeholder="HH:mm"
                css={styles.dropdownControl}
                disabled={!editable}
              />
            </div>
          </Margin>

          <Margin bottom={1} css={styles.row}>
            <Text as="label" size={11}>
              Create triggers
            </Text>
            <div style={{ width: '180px' }}>
              <FormInput
                oldInput
                name="createTriggers"
                type="select"
                placeholder="False"
                value={false}
                defaultValue={false}
                options={[
                  { value: true, label: <span style={{ fontSize: '10px', fontFamily: 'Montserrat, Arial, Helvetica, sans-serif' }}>True</span> },
                  { value: false, label: <span style={{ fontSize: '10px', fontFamily: 'Montserrat, Arial, Helvetica, sans-serif' }}>False</span> }
                ]}
                css={styles.dropdownControl}
                disabled={!editable}
              />
            </div>
          </Margin>
        </>
      )}
    </EditableFormPanel>
  )
}

EditEventFormComponent.propTypes = {
  form: PropTypes.shape(),
  header: PropTypes.string.isRequired,
  loadJurisdictions: PropTypes.func,
  loadTournaments: PropTypes.func,
  loadTeamsSearch: PropTypes.func,
  loadCategories: PropTypes.func,
  onSubmitRequest: PropTypes.func,
  onChangeTournament: PropTypes.func,
  teams: PropTypes.any,
}
export default EditEventFormComponent
